/**
 * Visibility helpers for the activity collection detail page.
 * A "filling timeline" requires at least two temporal readings
 * (super-container dual images at the same instant still count as one point).
 */

export const hasFillingTimeline = (historyCycle) =>
  Array.isArray(historyCycle) && historyCycle.length > 1;

export const ACTIVITY_DETAIL_DISPLAY_RULE_GROUPS = [
  "summary",
  "tabs",
  "filling",
  "sorting",
  "readings",
];

/**
 * Evaluates which detail sections are shown for the current collection.
 * `visible: true` means the section is rendered for this detail.
 */
export const evaluateActivityDetailDisplayRules = ({
  historyCycle = [],
  data = null,
  dataSortedByAnomalies = [],
  isSuperContainer = false,
} = {}) => {
  const fillingTimeline = hasFillingTimeline(historyCycle);
  const packmatActive = Boolean(data?.packmatActive);
  const sortingActive = Boolean(data?.activatedSortingErrorOption);
  const hasAnomalyStrip =
    fillingTimeline &&
    sortingActive &&
    Array.isArray(dataSortedByAnomalies) &&
    dataSortedByAnomalies.length > 0;

  return [
    {
      id: "summary.levelAtCollection",
      group: "summary",
      visible: fillingTimeline,
    },
    {
      id: "summary.weight",
      group: "summary",
      visible: true,
    },
    {
      id: "summary.quality",
      group: "summary",
      visible: true,
    },
    {
      id: "summary.cycleDuration",
      group: "summary",
      visible: fillingTimeline,
    },
    {
      id: "summary.fillingRecommendations",
      group: "summary",
      visible: fillingTimeline,
    },
    {
      id: "summary.fillingLevelChart",
      group: "summary",
      visible: fillingTimeline,
    },
    {
      id: "summary.keyMoments",
      group: "summary",
      visible: fillingTimeline,
    },
    {
      id: "tabs.summary",
      group: "tabs",
      visible: true,
    },
    {
      id: "tabs.filling",
      group: "tabs",
      visible: fillingTimeline,
    },
    {
      id: "tabs.sorting",
      group: "tabs",
      visible: true,
    },
    {
      id: "tabs.followUp",
      group: "tabs",
      visible: true,
    },
    {
      id: "filling.levelChart",
      group: "filling",
      visible: fillingTimeline,
    },
    {
      id: "filling.weightChart",
      group: "filling",
      visible: fillingTimeline,
    },
    {
      id: "filling.compaction",
      group: "filling",
      visible: fillingTimeline && packmatActive,
    },
    {
      id: "sorting.charts",
      group: "sorting",
      visible: sortingActive,
    },
    {
      id: "readings.anomalyStrip",
      group: "readings",
      visible: hasAnomalyStrip,
    },
    {
      id: "readings.imageCards",
      group: "readings",
      visible: Array.isArray(historyCycle) && historyCycle.length > 0,
    },
    {
      id: "readings.superContainerSideBySide",
      group: "readings",
      visible: Boolean(isSuperContainer),
    },
    {
      id: "readings.superContainerWasteAi",
      group: "readings",
      visible: Boolean(isSuperContainer),
    },
  ];
};

export const filterRecommendationsForDisplay = (
  recommendations = [],
  historyCycle,
) => {
  if (hasFillingTimeline(historyCycle)) {
    return recommendations;
  }
  return recommendations.filter(
    (recommendation) =>
      recommendation.scope !== "filling" &&
      recommendation.scope !== "compaction",
  );
};
